import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Clock, MapPin, Navigation, AlertTriangle, Check, Disc } from 'lucide-react';

interface DirectionsPanelProps {
  directions: google.maps.DirectionsResult | null;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isNavigating: boolean;
  onStartNavigation: () => void;
  onStopNavigation: () => void;
  currentStreet?: string | null;
  currentStep?: number;
}

const DirectionsPanel: React.FC<DirectionsPanelProps> = ({
  directions,
  isExpanded,
  onToggleExpand,
  isNavigating,
  onStartNavigation,
  onStopNavigation,
  currentStreet,
  currentStep = 0
}) => {
  const [showRouteOptions, setShowRouteOptions] = useState(false);

  if (!directions || !directions.routes || directions.routes.length === 0) {
    return null;
  }

  const route = directions.routes[0];
  const leg = route.legs[0];
  
  // Calculate progress percentage for the trip
  const totalDistance = leg.distance?.value || 0;
  const totalDuration = leg.duration?.value || 0;
  
  // Calculate remaining distance and time based on current step
  let remainingDistance = 0;
  let remainingDuration = 0;
  
  for (let i = currentStep; i < leg.steps.length; i++) {
    remainingDistance += leg.steps[i].distance?.value || 0;
    remainingDuration += leg.steps[i].duration?.value || 0;
  }
  
  // Format remaining time nicely
  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds} sec`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours} hr ${remainingMinutes} min`;
  };
  
  const formatDistance = (meters: number): string => {
    if (meters < 1000) return `${meters} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  };
  
  // Parse HTML instructions to clean text
  const cleanInstructions = (html: string): string => {
    return html.replace(/<\/?[^>]+(>|$)/g, "");
  };

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden w-full max-w-md">
      {/* Header with route summary */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Route to Destination</h3>
            <div className="flex items-center gap-2 mt-1">
              <Clock className="h-4 w-4 text-gray-600" />
              <span className="text-sm text-gray-700">
                {isNavigating ? formatTime(remainingDuration) : leg.duration?.text}
              </span>
              <span className="text-xs text-gray-500">
                ({isNavigating ? formatDistance(remainingDistance) : leg.distance?.text})
              </span>
            </div>
            {currentStreet && isNavigating && (
              <div className="text-xs text-blue-600 mt-1">
                Currently on: {currentStreet}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {!isNavigating ? (
              <button
                onClick={onStartNavigation}
                className="px-3 py-1.5 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors text-sm flex items-center gap-1"
              >
                <Navigation className="h-4 w-4" />
                <span>Start</span>
              </button>
            ) : (
              <button
                onClick={onStopNavigation}
                className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
              >
                Stop
              </button>
            )}
            <button
              onClick={onToggleExpand}
              className="p-1.5 text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
            >
              {isExpanded ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
        
        {/* Progress bar for the route */}
        {isNavigating && (
          <div className="mt-3">
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 rounded-full"
                style={{ 
                  width: `${Math.max(5, 100 - (remainingDistance / totalDistance * 100))}%` 
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-gray-500">
              <div>Start</div>
              <div>Destination</div>
            </div>
          </div>
        )}
      </div>

      {/* Next instruction highlight */}
      {isNavigating && (
        <div className="p-3 bg-blue-50 border-b border-blue-100">
          <div className="flex items-start gap-3">
            <div className="mt-1">
              <div className="h-7 w-7 rounded-full bg-blue-600 flex items-center justify-center">
                <Navigation className="h-4 w-4 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-blue-800">Next direction</div>
              <div 
                className="text-sm text-blue-700 font-medium" 
                dangerouslySetInnerHTML={{ 
                  __html: leg.steps[Math.min(currentStep, leg.steps.length - 1)].instructions 
                }} 
              />
              <div className="text-xs text-blue-600 mt-1">
                {leg.steps[Math.min(currentStep, leg.steps.length - 1)].distance?.text}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expanded step-by-step directions */}
      {isExpanded && (
        <div className="p-2 max-h-[50vh] overflow-y-auto">
          <div className="space-y-3">
            {/* Origin */}
            <div className="flex items-start gap-3 p-2">
              <div className="mt-1">
                <div className="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center">
                  <MapPin className="h-3.5 w-3.5 text-white" />
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Start</div>
                <div className="text-sm text-gray-600">{leg.start_address}</div>
              </div>
            </div>

            {/* Steps */}
            {leg.steps.map((step, index) => (
              <div 
                key={index} 
                className={`flex items-start gap-3 p-2 ${index === currentStep && isNavigating ? 'bg-blue-50' : 'hover:bg-gray-50'} rounded-md`}
              >
                <div className="flex flex-col items-center">
                  <div className={`h-6 w-6 rounded-full ${index === currentStep && isNavigating ? 'bg-blue-600' : 'bg-gray-200'} flex items-center justify-center`}>
                    {index === currentStep && isNavigating ? (
                      <Navigation className="h-3.5 w-3.5 text-white" />
                    ) : (
                      <span className="text-xs font-medium text-gray-700">{index + 1}</span>
                    )}
                  </div>
                  {index < leg.steps.length - 1 && (
                    <div className={`h-6 border-l ${index === currentStep && isNavigating ? 'border-blue-300' : 'border-gray-300'} mx-auto`}></div>
                  )}
                </div>
                <div className="flex-1">
                  <div 
                    className={`text-sm ${index === currentStep && isNavigating ? 'text-blue-700 font-medium' : 'text-gray-900'}`} 
                    dangerouslySetInnerHTML={{ __html: step.instructions }} 
                  />
                  <div className={`text-xs ${index === currentStep && isNavigating ? 'text-blue-600' : 'text-gray-500'} mt-1`}>
                    {step.distance?.text} · {step.duration?.text}
                  </div>
                </div>
              </div>
            ))}

            {/* Destination */}
            <div className="flex items-start gap-3 p-2">
              <div className="mt-1">
                <div className="h-6 w-6 rounded-full bg-red-600 flex items-center justify-center">
                  <MapPin className="h-3.5 w-3.5 text-white" />
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Destination</div>
                <div className="text-sm text-gray-600">{leg.end_address}</div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Route options */}
      {showRouteOptions && (
        <div className="p-3 bg-gray-50 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Route options</h4>
          <div className="space-y-2">
            <label className="flex items-center space-x-2 text-sm">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span>Avoid highways</span>
            </label>
            <label className="flex items-center space-x-2 text-sm">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span>Avoid tolls</span>
            </label>
          </div>
        </div>
      )}
      
      {/* Footer with options */}
      <div className="p-2 border-t border-gray-200 flex justify-between">
        <button 
          onClick={() => setShowRouteOptions(!showRouteOptions)}
          className="text-xs text-gray-600 hover:text-gray-900"
        >
          Route options
        </button>
        
        <div className="flex space-x-2">
          <button className="text-xs text-gray-600 hover:text-gray-900">
            Share
          </button>
          <span className="text-gray-300">|</span>
          <button className="text-xs text-gray-600 hover:text-gray-900">
            Report issues
          </button>
        </div>
      </div>
    </div>
  );
};

export default DirectionsPanel; 